// Index of a structurally valid document, shared by the semantic layers.
// Pure data built once; no layer mutates the document.

import type {
  AssetRef,
  CreativeDocument,
  CustomEventDecl,
  Element,
  FontRef,
  FormDecl,
  FormField,
  Layout,
  Link,
  Rule,
  Scene,
  SurveyQuestion,
  TimerDecl,
  VariableDecl,
} from "../schema/document.js";

export type ParentRef =
  | { readonly kind: "scene"; readonly id: string }
  | { readonly kind: "overlay"; readonly side: "below" | "above" }
  | { readonly kind: "group"; readonly id: string }
  | { readonly kind: "slot"; readonly id: string; readonly slot: string };

export interface Indexed<T> {
  readonly value: T;
  readonly index: number;
  readonly path: string;
}

export interface DocIndex {
  readonly doc: CreativeDocument;
  readonly layouts: ReadonlyMap<string, Indexed<Layout>>;
  readonly scenes: ReadonlyMap<string, Indexed<Scene>>;
  readonly elements: ReadonlyMap<string, Indexed<Element>>;
  readonly assets: ReadonlyMap<string, Indexed<AssetRef>>;
  readonly fonts: ReadonlyMap<string, Indexed<FontRef>>;
  readonly links: ReadonlyMap<string, Indexed<Link>>;
  readonly variables: ReadonlyMap<string, Indexed<VariableDecl>>;
  readonly timers: ReadonlyMap<string, Indexed<TimerDecl>>;
  readonly rules: ReadonlyMap<string, Indexed<Rule>>;
  readonly events: ReadonlyMap<string, Indexed<CustomEventDecl>>;
  readonly questions: ReadonlyMap<string, Indexed<SurveyQuestion>>;
  readonly forms: ReadonlyMap<string, Indexed<FormDecl>>;
  readonly fields: ReadonlyMap<string, ReadonlyMap<string, Indexed<FormField>>>;
  /** First parent seen per element (duplicates are reported by the reference layer). */
  readonly parents: ReadonlyMap<string, ParentRef>;
}

/** Builds id → entity maps, keeping the FIRST occurrence; duplicates are reported separately. */
function indexBy<T>(items: readonly T[] | undefined, key: (t: T) => string, base: string): Map<string, Indexed<T>> {
  const map = new Map<string, Indexed<T>>();
  (items ?? []).forEach((value, index) => {
    const k = key(value);
    if (!map.has(k)) map.set(k, { value, index, path: `${base}[${String(index)}]` });
  });
  return map;
}

export function buildIndex(doc: CreativeDocument): DocIndex {
  const fields = new Map<string, Map<string, Indexed<FormField>>>();
  (doc.forms ?? []).forEach((form, fi) => {
    if (!fields.has(form.id))
      fields.set(
        form.id,
        indexBy(form.fields, (f) => f.id, `$.forms[${String(fi)}].fields`)
      );
  });

  const parents = new Map<string, ParentRef>();
  const claim = (id: string, parent: ParentRef) => {
    if (!parents.has(id)) parents.set(id, parent);
  };
  for (const scene of doc.scenes) for (const child of scene.children) claim(child, { kind: "scene", id: scene.id });
  for (const child of doc.overlay?.below ?? []) claim(child, { kind: "overlay", side: "below" });
  for (const child of doc.overlay?.above ?? []) claim(child, { kind: "overlay", side: "above" });
  for (const el of doc.elements) {
    if (el.type === "group") for (const child of el.children) claim(child, { kind: "group", id: el.id });
    if (el.type === "component")
      for (const [slot, ids] of Object.entries(el.slots ?? {}))
        for (const child of ids) claim(child, { kind: "slot", id: el.id, slot });
  }

  return {
    doc,
    layouts: indexBy(doc.layouts, (l) => l.id, "$.layouts"),
    scenes: indexBy(doc.scenes, (s) => s.id, "$.scenes"),
    elements: indexBy(doc.elements, (e) => e.id, "$.elements"),
    assets: indexBy(doc.assets, (a) => a.key, "$.assets"),
    fonts: indexBy(doc.fonts, (f) => f.key, "$.fonts"),
    links: indexBy(doc.links, (l) => l.id, "$.links"),
    variables: indexBy(doc.variables, (v) => v.id, "$.variables"),
    timers: indexBy(doc.timers, (t) => t.id, "$.timers"),
    rules: indexBy(doc.rules, (r) => r.id, "$.rules"),
    events: indexBy(doc.events, (e) => e.name, "$.events"),
    questions: indexBy(doc.survey?.questions, (q) => q.id, "$.survey.questions"),
    forms: indexBy(doc.forms, (f) => f.id, "$.forms"),
    fields,
    parents,
  };
}

/** Root container (scene id or "overlay") of an element, following parents; undefined for orphans/cycles. */
export function rootOf(index: DocIndex, elementId: string): string | undefined {
  const seen = new Set<string>();
  let current = elementId;
  for (;;) {
    if (seen.has(current)) return undefined;
    seen.add(current);
    const parent = index.parents.get(current);
    if (!parent) return undefined;
    if (parent.kind === "scene") return parent.id;
    if (parent.kind === "overlay") return "overlay";
    current = parent.id;
  }
}

/** All element ids in the subtree of `rootIds` (depth-first, cycle-safe). */
export function subtree(index: DocIndex, rootIds: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    out.push(id);
    const el = index.elements.get(id)?.value;
    if (el?.type === "group") el.children.forEach(visit);
    if (el?.type === "component")
      Object.values(el.slots ?? {}).forEach((ids) => {
        ids.forEach(visit);
      });
  };
  rootIds.forEach(visit);
  return out;
}

/** Whether an element exists in a layout variant (onlyIn, inherited from ancestors). */
export function inLayout(index: DocIndex, elementId: string, layoutId: string): boolean {
  const seen = new Set<string>();
  let current: string | undefined = elementId;
  while (current !== undefined && !seen.has(current)) {
    seen.add(current);
    const el = index.elements.get(current)?.value;
    if (el?.onlyIn && !el.onlyIn.includes(layoutId)) return false;
    const parent = index.parents.get(current);
    current = parent && (parent.kind === "group" || parent.kind === "slot") ? parent.id : undefined;
  }
  return true;
}
